"use client";

import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * Error boundary for the registry section.
 *
 * The list pages degrade to an empty state on their own, but the detail pages
 * fetch a single document and have nothing to render without it. Without this,
 * an admin-web outage turns a deep link into the framework's default error page.
 * Nothing about the upstream failure is shown — the proxy routes already take
 * care not to leak it, and this must not undo that.
 */
export default function RegistryError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("registry page failed", error);
  }, [error]);

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-3 px-4 py-24 text-center">
      <AlertTriangle className="size-8 text-muted-foreground" />
      <h1 className="text-xl font-bold text-foreground">The registry is temporarily unavailable</h1>
      <p className="text-sm text-muted-foreground">
        This is on our side, not yours. Installs already running are unaffected — the installer
        retries.
      </p>
      <Button variant="outline" onClick={reset} className="mt-2">
        Try again
      </Button>
    </div>
  );
}
